import React, { useEffect, useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { formatApiError } from '../lib/errors';
import { Lock, Eye, EyeOff, CheckCircle2, Loader2 } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BG = 'https://images.unsplash.com/photo-1694880912546-5d9619757fbb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwyfHxsYXR2aWElMjBsYW5kc2NhcGUlMjBmb2d8ZW58MHx8fHwxNzg0MTcwOTc1fDA&ixlib=rb-4.1.0&q=85';

export default function ResetPasswordPage() {
  const { language } = useLanguage();
  const tr = (lv, en, ru) => (language === 'lv' ? lv : language === 'ru' ? ru : en);
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') || '';

  const [newPw, setNewPw] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) setError(tr('Saite nav derīga — trūkst žetona.', 'Invalid link — missing token.', 'Ссылка недействительна — отсутствует токен.'));
  }, [token, language]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (newPw.length < 8) { setError(tr('Parolei jābūt vismaz 8 simbolus garai', 'Password must be at least 8 characters', 'Пароль должен быть не менее 8 символов')); return; }
    if (newPw !== confirm) { setError(tr('Paroles nesakrīt', 'Passwords do not match', 'Пароли не совпадают')); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: newPw, confirm_password: confirm });
      setDone(true);
      setTimeout(() => navigate('/login'), 2500);
    } catch (err) {
      setError(formatApiError(err, tr('Kļūda. Mēģini vēlreiz.', 'Error. Try again.', 'Ошибка. Попробуйте снова.')));
    } finally {
      setLoading(false);
    }
  };

  const inputCls = 'w-full h-12 pl-11 pr-10 rounded-2xl bg-white/5 border border-white/10 text-white placeholder-white/40 text-sm focus:border-amber-500/60 focus:bg-white/[0.07] focus:outline-none transition-colors';

  return (
    <div className="fixed inset-0 flex items-center justify-center overflow-y-auto py-10 px-5" style={{ background: '#0A0D0B' }} data-testid="reset-password-page">
      <div className="absolute inset-0 z-0">
        <img src={BG} alt="" className="w-full h-full object-cover opacity-30 scale-105" />
        <div className="absolute inset-0" style={{ background: 'radial-gradient(120% 90% at 50% 8%, rgba(10,13,11,0.15), rgba(10,13,11,0.9))' }} />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 24, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        className="relative z-10 w-full max-w-md rounded-[28px] border border-white/10 bg-[#0A0D0B]/70 backdrop-blur-2xl shadow-[0_8px_40px_rgba(0,0,0,0.6)] p-8 sm:p-10"
        style={{ fontFamily: 'Manrope, sans-serif' }}
      >
        <div className="w-14 h-14 rounded-2xl bg-amber-500/15 flex items-center justify-center mb-5">
          <Lock className="w-7 h-7 text-amber-400" />
        </div>
        <h1 className="text-white text-3xl sm:text-4xl font-medium tracking-tight mb-2" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
          {tr('Jauna parole', 'New password', 'Новый пароль')}
        </h1>
        <p className="text-white/60 text-sm mb-8">{tr('Izvēlies jaunu paroli savam KoLV kontam.', 'Choose a new password for your KoLV account.', 'Выберите новый пароль для вашего аккаунта KoLV.')}</p>

        {done ? (
          <div className="flex items-start gap-3 p-4 rounded-2xl bg-green-500/10 border border-green-500/30" data-testid="reset-password-success">
            <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-green-200/90">
              <p className="font-semibold mb-1 text-green-300">{tr('Parole nomainīta!', 'Password changed!', 'Пароль изменён!')}</p>
              <p>{tr('Pāradresējam uz ieiešanu...', 'Redirecting to login...', 'Перенаправляем ко входу...')}</p>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="p-3 rounded-2xl bg-red-500/10 border border-red-500/30 text-red-300 text-sm" data-testid="reset-error">{error}</div>
            )}
            <div>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                <input
                  type={showPw ? 'text' : 'password'}
                  value={newPw}
                  onChange={(e) => setNewPw(e.target.value)}
                  required
                  minLength={8}
                  placeholder={tr('Jaunā parole', 'New password', 'Новый пароль')}
                  className={inputCls}
                  data-testid="new-password-input"
                />
                <button type="button" onClick={() => setShowPw((s) => !s)} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-white/40 hover:text-white transition-colors">
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-xs text-white/40 mt-1.5 ml-1">{tr('Vismaz 8 simboli', 'At least 8 characters', 'Не менее 8 символов')}</p>
            </div>
            <div className="relative">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
              <input
                type={showPw ? 'text' : 'password'}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
                minLength={8}
                placeholder={tr('Apstiprini paroli', 'Confirm password', 'Подтвердите пароль')}
                className={inputCls}
                data-testid="confirm-password-input"
              />
            </div>
            <button
              type="submit"
              disabled={loading || !token}
              className="w-full h-12 rounded-full bg-amber-500 text-[#0A0D0B] font-bold text-sm flex items-center justify-center gap-2 hover:bg-amber-400 transition-colors disabled:opacity-60"
              style={{ boxShadow: '0 8px 30px rgba(255,159,28,0.35)' }}
              data-testid="reset-submit-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : tr('Atiestatīt paroli', 'Reset password', 'Сбросить пароль')}
            </button>
            <div className="text-center">
              <Link to="/login" className="text-sm text-amber-400 hover:underline">{tr('Atpakaļ uz ieiešanu', 'Back to login', 'Назад ко входу')}</Link>
            </div>
          </form>
        )}
      </motion.div>
    </div>
  );
}
